/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { BehaviorSubject, combineLatest, map, shareReplay, switchMap } from 'rxjs';
import { DimensionElement } from '../../constants';
import { PropertyService } from '../../services';
import { getOrderedAssignments, OrderedSetAssignment } from '../property-set-input/property-set-input.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-property-set-values',
  templateUrl: './property-set-values.component.html',
  standalone: false,
})
export class PropertySetValuesComponent {
  /**
   * Requests in flight, shared across instances. A list of cards mounts many of these at once for the
   * same property set; without this each one fires its own identical requests. Entries drop once the
   * request settles, so later loads still get fresh data.
   */
  private static inFlight = new Map<string, Promise<unknown>>();

  constructor(private propertySvc: PropertyService) {}

  private static shared<T>(key: string, load: () => Promise<T>): Promise<T> {
    const pending = PropertySetValuesComponent.inFlight.get(key) as Promise<T> | undefined;
    if (pending) return pending;

    const request = load().finally(() => PropertySetValuesComponent.inFlight.delete(key));
    PropertySetValuesComponent.inFlight.set(key, request);
    return request;
  }

  @PropertyListener('propertySetId') propertySetId$ = new BehaviorSubject<string | null>(null);
  @Input() propertySetId!: string;

  @PropertyListener('propertyValues') propertyValues$ = new BehaviorSubject<Record<string, any>>({});
  @Input() propertyValues!: Record<string, any>;

  @PropertyListener('dimensionValues') dimensionValues$ = new BehaviorSubject<Record<string, unknown>>({});
  @Input() dimensionValues: Record<string, unknown> = {};

  @PropertyListener('showHidden') showHidden$ = new BehaviorSubject(false);
  @Input() showHidden = false;

  @Input() firstOnly = false;

  /** When true, a placeholder is shown until the property set has loaded, instead of rendering nothing. */
  @Input() showLoading = false;

  /** When true, required fields with no value are listed (e.g. checkout asset preview). Off by default for read-only views. */
  @PropertyListener('showMissingRequired') showMissingRequired$ = new BehaviorSubject(false);
  @Input() showMissingRequired = false;

  propertySet$ = this.propertySetId$.pipe(
    switchMap(async propertySetId => {
      if (!propertySetId) return null;
      return await PropertySetValuesComponent.shared(`set|${propertySetId}`, () =>
        this.propertySvc.retrievePropertySet(propertySetId)
      );
    }),
    shareReplay(1)
  );

  dimensionElements$ = this.propertySet$.pipe(
    switchMap(async propertySet => {
      const elementsByDimensionId: Record<string, DimensionElement[]> = {};
      const categoricalAssignments = (propertySet?.dimensions ?? []).filter(
        assignment => assignment.dimension && !assignment.dimension.isScalar
      );

      await Promise.all(
        categoricalAssignments.map(async assignment => {
          try {
            elementsByDimensionId[assignment.dimensionId] = await PropertySetValuesComponent.shared(
              `elements|${assignment.dimensionId}`,
              () => this.propertySvc.listDimensionElements(assignment.dimensionId)
            );
          } catch (err) {
            console.error(`Failed to load elements for dimension "${assignment.dimensionId}":`, err);
            elementsByDimensionId[assignment.dimensionId] = [];
          }
        })
      );

      return elementsByDimensionId;
    })
  );

  viewModel$ = combineLatest([
    this.propertySet$,
    this.propertyValues$,
    this.dimensionValues$,
    this.dimensionElements$,
    this.showHidden$,
    this.showMissingRequired$,
  ]).pipe(
    map(([propertySet, propertyValues, dimensionValues, dimensionElements, showHidden, showMissingRequired]) => {
      if (!propertySet) return null;

      const orderedAssignments = getOrderedAssignments(propertySet).filter(assignment =>
        this.isVisibleAssignment(assignment, showHidden)
      );

      const missing = showMissingRequired
        ? PropertySet.getMissingRequiredFields(propertySet, propertyValues ?? {}, dimensionValues ?? {})
        : [];
      const missingIds = new Set(missing.map(field => field.id));

      return {
        propertySet,
        orderedAssignments,
        propertyValues: propertyValues ?? {},
        dimensionValues: dimensionValues ?? {},
        dimensionElements,
        missingIds,
        missing,
        showMissingRequired,
      };
    })
  );

  isMissingValue = PropertySet.isMissingValue;

  private isVisibleAssignment(assignment: OrderedSetAssignment, showHidden: boolean): boolean {
    if (assignment.kind === 'PROPERTY') {
      return showHidden || !assignment.propertyAssignment.hidden;
    }
    return showHidden || !assignment.dimensionAssignment.hidden;
  }

  displayDimensionValue(
    dimensionId: string,
    value: unknown,
    dimensionElements: Record<string, DimensionElement[]>
  ): string {
    if (PropertySet.isMissingValue(value)) return '';
    const element = dimensionElements[dimensionId]?.find(el => el.id === value);
    return element?.title ?? String(value);
  }
}
